<script setup lang="ts">
import { CountSelect, FormRow, FormSection } from '@it-tools/shell'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { STORAGE_CLASSES } from '../model/priceBook'
import {
  isValidGb,
  LIMITS,
  MEMORY_OPTIONS,
  OPERATING_SYSTEMS,
  type Resource,
  VCPU_OPTIONS,
} from '../model/spec'
import NumberField from './NumberField.vue'

/** One resource line: the fields its kind has, and a way to remove it. */
const props = defineProps<{
  modelValue: Resource
  /** Position among lines of the same kind, so "Remove Virtual machine 2" is unambiguous. */
  index: number
}>()
const emit = defineEmits<{ 'update:modelValue': [Resource]; remove: [] }>()

const { t, n } = useI18n()

// Unique per line, and stable while other lines are added or removed.
const id = (field: string): string => `r${props.modelValue.id}-${field}`
const name = computed(() => t(`kind.${props.modelValue.kind}`))
const gbError = computed(() => t('form.gbInvalid', { max: n(LIMITS.gb) }))

function update(patch: Partial<Resource>): void {
  emit('update:modelValue', { ...props.modelValue, ...patch } as Resource)
}

/** The value of a select whose options are `options`, or undefined for anything else. */
function chosen<T extends string | number>(event: Event, options: readonly T[]): T | undefined {
  const target = event.target
  if (!(target instanceof HTMLSelectElement)) return undefined
  return options.find((option) => String(option) === target.value)
}

function onSelect<T extends string | number>(
  event: Event,
  options: readonly T[],
  apply: (value: T) => Partial<Resource>,
): void {
  const value = chosen(event, options)
  if (value !== undefined) update(apply(value))
}

const AVAILABILITY = ['single', 'ha'] as const
</script>

<template>
  <div class="resource" :data-kind="modelValue.kind">
    <FormSection :legend="name">
      <button
        type="button"
        class="resource__remove"
        :aria-label="t('form.removeNamed', { resource: name, index })"
        @click="emit('remove')"
      >
        <span aria-hidden="true">×</span>
        <span class="resource__remove-text">{{ t('form.remove') }}</span>
      </button>

      <FormRow
        v-if="modelValue.kind === 'vm' || modelValue.kind === 'db'"
        :label="t('form.size')"
        :for-id="id('vcpu')"
      >
        <select
          :id="id('vcpu')"
          class="mono"
          :value="modelValue.vcpu"
          :aria-label="t('form.vcpu')"
          @change="onSelect($event, VCPU_OPTIONS, (vcpu) => ({ vcpu }))"
        >
          <option v-for="value in VCPU_OPTIONS" :key="value" :value="value">
            {{ t('form.vcpuValue', { value: n(value) }) }}
          </option>
        </select>
        <select
          :id="id('memory')"
          class="mono"
          :value="modelValue.memoryGb"
          :aria-label="t('form.memory')"
          @change="onSelect($event, MEMORY_OPTIONS, (memoryGb) => ({ memoryGb }))"
        >
          <option v-for="value in MEMORY_OPTIONS" :key="value" :value="value">
            {{ t('form.gbValue', { value: n(value) }) }}
          </option>
        </select>
      </FormRow>

      <template v-if="modelValue.kind === 'vm'">
        <FormRow :label="t('form.os')" :for-id="id('os')">
          <select
            :id="id('os')"
            :value="modelValue.os"
            @change="onSelect($event, OPERATING_SYSTEMS, (os) => ({ os }))"
          >
            <option v-for="os in OPERATING_SYSTEMS" :key="os" :value="os">
              {{ t(`os.${os}`) }}
            </option>
          </select>
        </FormRow>
        <FormRow :label="t('form.running')" :for-id="id('hours')">
          <CountSelect
            :id="id('hours')"
            :model-value="modelValue.hoursPerDay"
            :min="LIMITS.minHoursPerDay"
            :max="LIMITS.hoursPerDay"
            :label="t('form.running')"
            @update:model-value="(hoursPerDay) => update({ hoursPerDay })"
          />
          <span class="form-row__muted">{{ t('form.hoursUnit') }}</span>
        </FormRow>
      </template>

      <template v-if="modelValue.kind === 'db'">
        <FormRow :label="t('form.storage')" :for-id="id('storage')">
          <NumberField
            :id="id('storage')"
            :model-value="modelValue.storageGb"
            :unit="t('form.gb')"
            :valid="isValidGb"
            :error="gbError"
            @update:model-value="(storageGb) => update({ storageGb })"
          />
        </FormRow>
        <FormRow :label="t('form.availability')" :for-id="id('ha')">
          <select
            :id="id('ha')"
            :value="modelValue.ha ? 'ha' : 'single'"
            @change="onSelect($event, AVAILABILITY, (value) => ({ ha: value === 'ha' }))"
          >
            <option v-for="value in AVAILABILITY" :key="value" :value="value">
              {{ t(`form.${value}`) }}
            </option>
          </select>
        </FormRow>
      </template>

      <FormRow
        v-if="modelValue.kind === 'disk' || modelValue.kind === 'bucket'"
        :label="t(modelValue.kind === 'disk' ? 'form.diskSize' : 'form.stored')"
        :for-id="id('size')"
      >
        <NumberField
          :id="id('size')"
          :model-value="modelValue.sizeGb"
          :unit="t('form.gb')"
          :valid="isValidGb"
          :error="gbError"
          @update:model-value="(sizeGb) => update({ sizeGb })"
        />
      </FormRow>

      <FormRow
        v-if="modelValue.kind === 'bucket'"
        :label="t('form.storageClass')"
        :for-id="id('class')"
      >
        <select
          :id="id('class')"
          :value="modelValue.storageClass"
          @change="onSelect($event, STORAGE_CLASSES, (storageClass) => ({ storageClass }))"
        >
          <option v-for="value in STORAGE_CLASSES" :key="value" :value="value">
            {{ t(`storageClass.${value}`) }}
          </option>
        </select>
      </FormRow>

      <FormRow v-if="modelValue.kind !== 'bucket'" :label="t('form.count')" :for-id="id('count')">
        <CountSelect
          :id="id('count')"
          :model-value="modelValue.count"
          :min="1"
          :max="LIMITS.count"
          :label="t('form.count')"
          @update:model-value="(count) => update({ count })"
        />
      </FormRow>
    </FormSection>
  </div>
</template>

<style scoped>
.resource {
  position: relative;
}

/* Top right, on the line of the legend: present on every card, never in the way. */
.resource__remove {
  position: absolute;
  top: var(--space-3);
  right: var(--space-4);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-muted);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.resource__remove:hover {
  border-color: var(--color-danger);
  background: var(--color-danger-subtle);
  color: var(--color-danger);
}

@media (max-width: 36rem) {
  .resource__remove-text {
    display: none;
  }
}
</style>
